import {
    AlertCircle, Check, ChevronDown, ChevronUp, Clock, Copy, DollarSign,
    ExternalLink, Loader, Package, Pencil, Search, X,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import {
    getInboundShortages, getOverdueShipments,
    listReimbursementCases, createReimbursementCase, updateReimbursementCase, deleteReimbursementCase,
    enrichReimbursementLink,
} from "../services/reimbursementService";
import useLiveShipmentSync from "../hooks/useLiveShipmentSync";

const ProductImage = ({ src, fallback = "📦" }) => {
    const [errored, setErrored] = useState(false);
    if (!src || errored) return <span className="text-2xl">{fallback}</span>;
    return <img src={src} alt="" onError={() => setErrored(true)} className="w-10 h-10 object-contain rounded" />;
};

const money = (v) => `$${(v || 0).toFixed(2)}`;

const CASE_STATUS_STYLES = {
    identified: "bg-gray-100 text-gray-500",
    filed: "bg-blue-50 text-blue-600",
    resolved: "bg-green-50 text-green-600",
    denied: "bg-red-50 text-red-600",
    ignored: "bg-gray-100 text-gray-400",
};

const SELLER_CENTER_URL = "https://seller.walmart.com/";

// Buckets shortage rows by wfs_shipment_split_id — one split IS one
// physical shipment to one FC, so this is the correct "same shipment"
// grouping key (not shipment_id alone, in case that's ever reused, and
// not inbound_order_id, which can span multiple FC destinations/splits).
const groupShortagesByShipment = (shortages) => {
    const groups = new Map();
    for (const s of shortages) {
        const key = s.wfs_shipment_split_id;
        if (!groups.has(key)) {
            groups.set(key, {
                wfs_shipment_split_id: s.wfs_shipment_split_id,
                inbound_order_id: s.inbound_order_id,
                shipment_id: s.shipment_id,
                ship_to_address: s.ship_to_address,
                split_status: s.split_status,
                order_created_date: s.order_created_date,
                updated_expected_delivery_date: s.updated_expected_delivery_date,
                actual_delivery_date: s.actual_delivery_date,
                carrier_name: s.carrier_name,
                tracking_no: s.tracking_no,
                items: [],
                total_estimated_value: 0,
                total_discrepancy_qty: 0,
                any_missing_price: false,
            });
        }
        const g = groups.get(key);
        g.items.push(s);
        g.total_estimated_value += s.estimated_value || 0;
        g.total_discrepancy_qty += s.discrepancy_qty || 0;
        if (s.missing_price) g.any_missing_price = true;
    }
    // Map preserves insertion order, and `shortages` already arrives
    // sorted (most-recent-delivery-first) from the backend, so groups
    // naturally inherit that same order via their first-seen item.
    return Array.from(groups.values());
};

// Wraps a single ungrouped shortage into the same { ...shipment fields,
// items: [...] } shape a real group has, so EvidencePacketModal only
// needs one code path regardless of which mode triggered it.
const singleItemGroup = (s) => ({
    wfs_shipment_split_id: s.wfs_shipment_split_id,
    inbound_order_id: s.inbound_order_id,
    shipment_id: s.shipment_id,
    ship_to_address: s.ship_to_address,
    split_status: s.split_status,
    order_created_date: s.order_created_date,
    updated_expected_delivery_date: s.updated_expected_delivery_date,
    actual_delivery_date: s.actual_delivery_date,
    carrier_name: s.carrier_name,
    tracking_no: s.tracking_no,
    items: [s],
});

const buildEvidencePacket = (group) => {
    const totalValue = group.items.reduce((sum, s) => sum + (s.estimated_value || 0), 0);
    const totalQty = group.items.reduce((sum, s) => sum + (s.discrepancy_qty || 0), 0);
    const multi = group.items.length > 1;

    const header = "WFS INBOUND SHIPMENT SHORTAGE - REIMBURSEMENT REQUEST\n\n"
        + "Inbound Order ID: " + group.inbound_order_id + "\n"
        + "Shipment ID: " + group.shipment_id + "\n"
        + "Shipment Status: " + group.split_status + " (fully processed, no further units expected)\n"
        + "Destination FC: " + (group.ship_to_address?.fcName || "-") + "\n"
        + "FC Address: " + (group.ship_to_address?.city || "") + ", " + (group.ship_to_address?.stateCode || "") + "\n"
        + (group.carrier_name ? "Carrier: " + group.carrier_name + "\n" : "")
        + (group.tracking_no?.length ? "Tracking: " + group.tracking_no.join(", ") + "\n" : "")
        + (group.actual_delivery_date ? "Delivered: " + group.actual_delivery_date.slice(0, 10) + "\n" : "")
        + "\n";

    const itemBlocks = group.items.map((s, idx) =>
        (multi ? `Item ${idx + 1}:\n` : "")
        + `Product: ${s.title}\n`
        + `SKU: ${s.sku}\n`
        + `Units Shipped: ${s.shipped_qty}\n`
        + `Units Received: ${s.received_qty}\n`
        + `Units Short: ${s.discrepancy_qty}\n`
        + `Estimated Value: ${money(s.estimated_value)} (${s.discrepancy_qty} unit(s) x ${money(s.unit_price)}/unit sale price)\n`
    ).join("\n");

    const summary = multi
        ? `\nTotal Units Short Across This Shipment: ${totalQty}\nTotal Estimated Value: ${money(totalValue)}\n\n`
        : "\n";

    const request = "Request: Please investigate this inbound shipment discrepancy and issue reimbursement for the "
        + (multi ? "items listed above" : `${totalQty} unit(s)`)
        + ", confirmed shipped per the carrier pickup but not received into inventory at the destination fulfillment center.";

    return header + itemBlocks + summary + request;
};

// One labeled field + its own copy button — the building block for the
// "Data Points" tab. value is stringified as-is; label stays exactly as
// specified in the plain-text packet order below.
const CopyableField = ({ label, value }) => {
    const [copied, setCopied] = useState(false);
    if (value === null || value === undefined || value === "") return null;
    const displayValue = String(value);
    return (
        <div className="flex items-center justify-between gap-2 py-1.5 border-b border-gray-50 last:border-b-0">
            <div className="min-w-0">
                <p className="text-[10px] font-bold text-gray-400 uppercase">{label}</p>
                <p className="text-sm text-gray-800 break-words">{displayValue}</p>
            </div>
            <button
                onClick={() => { navigator.clipboard.writeText(displayValue); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
                className="flex-shrink-0 text-xs font-bold text-teal-600 hover:text-teal-800 inline-flex items-center gap-1"
            >
                {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
            </button>
        </div>
    );
};

const EvidencePacketModal = ({ group, onClose, onTracked }) => {
    const [copied, setCopied] = useState(false);
    const [copiedAll, setCopiedAll] = useState(false);
    const [tracking, setTracking] = useState(false);
    const [viewMode, setViewMode] = useState("full"); // 'full' | 'datapoints'
    const packet = buildEvidencePacket(group);
    const totalValue = group.items.reduce((sum, s) => sum + (s.estimated_value || 0), 0);
    const totalQty = group.items.reduce((sum, s) => sum + (s.discrepancy_qty || 0), 0);
    const multi = group.items.length > 1;

    // Whether paperwork is required — Walmart Preferred Carrier PARCEL
    // bookings don't need it; LTL/FTL (freight/truck) shipments do.
    const carrierType = group.items[0]?.carrier_quote_type || null;
    const needsPaperwork = carrierType === "LTL" || carrierType === "FTL";
    const isWpcParcel = carrierType === "Parcel";

    const copyPacket = () => {
        navigator.clipboard.writeText(packet);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // Builds the same fields shown in the Data Points tab into one flat
    // key: value text block, in the same order as the plain-text packet.
    const buildDataPointsText = () => {
        const lines = [
            `Inbound Order ID: ${group.inbound_order_id || ""}`,
            `Shipment ID: ${group.shipment_id || ""}`,
            `Shipment Status: ${group.split_status || ""}`,
            `Destination FC: ${group.ship_to_address?.fcName || ""}`,
            `FC City: ${group.ship_to_address?.city || ""}`,
            `FC State: ${group.ship_to_address?.stateCode || ""}`,
            `Carrier: ${group.carrier_name || ""}`,
            `Carrier Service Type: ${carrierType || ""}`,
            `Tracking Number(s): ${group.tracking_no?.length ? group.tracking_no.join(", ") : ""}`,
            `Delivered Date: ${group.actual_delivery_date ? group.actual_delivery_date.slice(0, 10) : ""}`,
            `Shipped Date: ${group.order_created_date ? group.order_created_date.slice(0, 10) : ""}`,
            "",
        ];
        group.items.forEach((s, idx) => {
            if (multi) lines.push(`Item ${idx + 1}:`);
            lines.push(
                `Product: ${s.title || ""}`,
                `SKU: ${s.sku || ""}`,
                `Units Shipped: ${s.shipped_qty ?? ""}`,
                `Units Received: ${s.received_qty ?? ""}`,
                `Units Short: ${s.discrepancy_qty ?? ""}`,
                `Unit Sale Price: ${money(s.unit_price)}`,
                `Estimated Value: ${money(s.estimated_value)}`,
                "",
            );
        });
        if (multi) {
            lines.push(`Total Units Short: ${totalQty}`, `Total Estimated Value: ${money(totalValue)}`);
        }
        return lines.join("\n");
    };

    const copyAllDataPoints = () => {
        navigator.clipboard.writeText(buildDataPointsText());
        setCopiedAll(true);
        setTimeout(() => setCopiedAll(false), 2000);
    };

    // One case row per item is still stored on the backend (cases are
    // keyed by SKU), but the seller only takes one action here for the
    // whole shipment — this fires them all together.
    const trackGroup = async () => {
        setTracking(true);
        try {
            await Promise.all(group.items.map((s) => createReimbursementCase({
                source_type: "inbound_shortage",
                wfs_shipment_split_id: s.wfs_shipment_split_id,
                inbound_order_id: s.inbound_order_id, shipment_id: s.shipment_id,
                sku: s.sku, title: s.title, image_url: s.image_url,
                discrepancy_qty: s.discrepancy_qty, unit_cost: s.unit_price,
                estimated_value: s.estimated_value, status: "filed",
                ship_to_address: s.ship_to_address, split_status: s.split_status,
                actual_delivery_date: s.actual_delivery_date, order_created_date: s.order_created_date,
                carrier_name: s.carrier_name, tracking_no: s.tracking_no,
            })));
            onTracked();
            onClose();
        } catch (e) {
            alert(e.message || "Failed to save");
        } finally {
            setTracking(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[88vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                    <div>
                        <h2 className="font-bold text-gray-800">Evidence Packet</h2>
                        {group.items.length > 1 && (
                            <p className="text-xs text-gray-400">{group.items.length} items · {money(totalValue)} combined</p>
                        )}
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                </div>

                <div className="px-6 pt-3 flex gap-1 flex-shrink-0 border-b border-gray-100">
                    <button onClick={() => setViewMode("full")}
                        className={`px-4 py-2 text-sm font-bold border-b-2 transition ${viewMode === "full" ? "border-teal-500 text-teal-700" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
                        Full Evidence
                    </button>
                    <button onClick={() => setViewMode("datapoints")}
                        className={`px-4 py-2 text-sm font-bold border-b-2 transition ${viewMode === "datapoints" ? "border-teal-500 text-teal-700" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
                        Data Points
                    </button>
                </div>

                <div className="p-5 overflow-y-auto flex-1">
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 text-xs text-blue-700">
                        <p className="font-bold mb-2">Walmart has no self-serve API for this claim type - file it in Seller Center directly:</p>
                        <ol className="list-decimal list-inside space-y-1 mb-3">
                            <li>Log in to <a href={SELLER_CENTER_URL} target="_blank" rel="noopener noreferrer" className="underline font-semibold inline-flex items-center gap-0.5">Seller Center <ExternalLink size={10} /></a></li>
                            <li>Click the <strong>?</strong> Help icon in the top navigation bar</li>
                            <li>Choose <strong>Contact Support</strong>, then describe the issue below</li>
                            <li>Pick the suggested <strong>Fulfillment / WFS</strong> category</li>
                            <li>Paste the info below into the case description</li>
                        </ol>
                        <a href={SELLER_CENTER_URL} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-300 rounded-lg font-bold text-blue-700 hover:bg-blue-100 transition">
                            Open Seller Center <ExternalLink size={12} />
                        </a>
                    </div>

                    {viewMode === "full" ? (
                        <pre className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs whitespace-pre-wrap font-mono text-gray-700">{packet}</pre>
                    ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                            <CopyableField label="Inbound Order ID" value={group.inbound_order_id} />
                            <CopyableField label="Shipment ID" value={group.shipment_id} />
                            <CopyableField label="Shipment Status" value={group.split_status} />
                            <CopyableField label="Destination FC" value={group.ship_to_address?.fcName} />
                            <CopyableField label="FC City" value={group.ship_to_address?.city} />
                            <CopyableField label="FC State" value={group.ship_to_address?.stateCode} />
                            <CopyableField label="Carrier" value={group.carrier_name} />
                            <CopyableField label="Carrier Service Type" value={carrierType} />
                            <CopyableField label="Tracking Number(s)" value={group.tracking_no?.length ? group.tracking_no.join(", ") : null} />
                            <CopyableField label="Delivered Date" value={group.actual_delivery_date?.slice(0, 10)} />
                            <CopyableField label="Shipped Date" value={group.order_created_date?.slice(0, 10)} />

                            {group.items.map((s, idx) => (
                                <div key={s.sku} className="mt-3 pt-3 border-t border-gray-200">
                                    {multi && <p className="text-[11px] font-bold text-gray-500 mb-1">Item {idx + 1}</p>}
                                    <CopyableField label="Product" value={s.title} />
                                    <CopyableField label="SKU" value={s.sku} />
                                    <CopyableField label="Units Shipped" value={s.shipped_qty} />
                                    <CopyableField label="Units Received" value={s.received_qty} />
                                    <CopyableField label="Units Short" value={s.discrepancy_qty} />
                                    <CopyableField label="Unit Sale Price" value={money(s.unit_price)} />
                                    <CopyableField label="Estimated Value" value={money(s.estimated_value)} />
                                </div>
                            ))}

                            {multi && (
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                    <CopyableField label="Total Units Short" value={totalQty} />
                                    <CopyableField label="Total Estimated Value" value={money(totalValue)} />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="px-5 py-4 border-t border-gray-100 flex gap-2 flex-shrink-0">
                    {viewMode === "full" ? (
                        <button onClick={copyPacket} className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-200 transition flex items-center justify-center gap-1.5">
                            {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy Whole Packet</>}
                        </button>
                    ) : (
                        <button onClick={copyAllDataPoints} className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-200 transition flex items-center justify-center gap-1.5">
                            {copiedAll ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy All Fields</>}
                        </button>
                    )}
                    <button onClick={trackGroup} disabled={tracking} className="flex-1 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-bold hover:bg-teal-700 transition disabled:opacity-50">
                        {tracking ? "Saving..." : "I Filed It - Track This Case"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Single-shipment card used when "Group by Shipment" is on — one combined
// row per split id, with an expandable list of the individual short items
// underneath.
const ShipmentGroupCard = ({ group, liveSync, onOpenPacket, onDismissGroup, dismissingGroupId }) => {
    const [expanded, setExpanded] = useState(true);
    const isDismissing = dismissingGroupId === group.wfs_shipment_split_id;

    return (
        <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="p-4 flex items-center gap-3">
                <button onClick={() => setExpanded((e) => !e)} className="flex-1 min-w-0 flex items-center gap-3 text-left">
                    <Package size={20} className="text-gray-300 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                            {group.ship_to_address?.fcName || "Unknown FC"} · Shipment {group.shipment_id}
                            {!liveSync.done && !liveSync.touchedIds.has(group.inbound_order_id) && (
                                <span className="flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-600 px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap">
                                    <Loader size={12} className="animate-spin" /> Updating…
                                </span>
                            )}
                        </p>
                        <p className="text-xs text-gray-400">{group.items.length} item{group.items.length > 1 ? "s" : ""} short · Inbound Order {group.inbound_order_id}</p>
                    </div>
                </button>
                <div className="text-right flex-shrink-0">
                    <p className="text-lg font-bold text-teal-700">{money(group.total_estimated_value)}</p>
                    <p className="text-[10px] text-gray-400 mb-1">{group.items.length > 1 ? "combined value" : `${money(group.items[0].unit_price)}/unit`}</p>
                    <button onClick={() => onOpenPacket(group)} className="text-xs font-bold text-teal-600 hover:text-teal-700 underline decoration-dotted block mb-1">
                        Get Evidence Packet
                    </button>
                    <button onClick={() => onDismissGroup(group)} disabled={isDismissing}
                        className="text-[10px] text-gray-300 hover:text-red-500 transition">
                        {isDismissing ? "..." : `Ignore / Remove All`}
                    </button>
                </div>
                <button onClick={() => setExpanded((e) => !e)} className="text-gray-300 hover:text-gray-500 flex-shrink-0">
                    {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
            </div>

            {group.any_missing_price && (
                <div className="mx-4 mb-2 bg-orange-50 border border-orange-200 rounded-lg px-2.5 py-1.5 text-[11px] text-orange-700 flex items-center gap-1.5">
                    <AlertCircle size={12} /> One or more items here have no sale price on file — shown as $0.00 until set in Catalog.
                </div>
            )}

            {expanded && (
                <div className="px-4 pb-3 space-y-2 border-t border-gray-50 pt-3">
                    {group.items.map((s) => (
                        <div key={s.sku} className="flex items-center gap-3 text-xs">
                            <ProductImage src={s.image_url} />
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-700 truncate">{s.title}</p>
                                <p className="text-gray-400">SKU {s.sku}</p>
                                <p className="text-red-500 font-semibold">Shipped {s.shipped_qty} → Received {s.received_qty} ({s.discrepancy_qty} short)</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                                <p className="font-bold text-gray-700">{money(s.estimated_value)}</p>
                                {s.missing_price && <p className="text-orange-500 text-[10px]">no price set</p>}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="px-4 pb-3 pt-2 border-t border-gray-50 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-400">
                <span>Status: <span className="font-semibold text-gray-600">{group.split_status}</span>{group.split_status === "CLOSED" && " (finalized — no further units expected)"}</span>
                {group.actual_delivery_date && <span>Delivered {group.actual_delivery_date.slice(0, 10)}</span>}
                {group.updated_expected_delivery_date && !group.actual_delivery_date && <span>ETA was {group.updated_expected_delivery_date.slice(0, 10)}</span>}
                {group.carrier_name && <span>Carrier: {group.carrier_name}</span>}
                {group.order_created_date && <span>Shipped {group.order_created_date.slice(0, 10)}</span>}
            </div>
        </div>
    );
};

const InboundShortagesTab = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true); // true only until the very first load ever completes
    const [refreshing, setRefreshing] = useState(false); // true for every load AFTER that — keeps filters/results mounted instead of blanking the screen
    const [initialScanDone, setInitialScanDone] = useState(false);
    const [packetFor, setPacketFor] = useState(null); // now always a "group" shape, single or multi-item
    const [dismissing, setDismissing] = useState(null); // ungrouped mode: shortage key currently being dismissed
    const [dismissingGroupId, setDismissingGroupId] = useState(null); // grouped mode: split id being dismissed
    const [groupByShipment, setGroupByShipment] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [stockFilter, setStockFilter] = useState("all"); // all | zero | partial
    const [statusFilter, setStatusFilter] = useState("CLOSED");
    const [minDays, setMinDays] = useState(10);
    const [maxDays, setMaxDays] = useState(50);
    const [minDaysDraft, setMinDaysDraft] = useState("10");
    const [maxDaysDraft, setMaxDaysDraft] = useState("50");
    const [visibleCount, setVisibleCount] = useState(20);
    const liveSync = useLiveShipmentSync();
    const sentinelRef = useRef(null);
    // Bumped on every load() call; a response only gets applied if its
    // sequence number still matches the latest — stops a slow, stale
    // request (e.g. from a filter value the user already changed away
    // from) from landing late and overwriting newer, correct results.
    const requestSeqRef = useRef(0);

    // Generous cushion between a shipment's CREATION date (the only thing
    // Get Shipments lets us bound the query by) and its actual DELIVERY
    // date (what maxDays actually filters on) — WFS transit + receiving
    // can take a few weeks. A shipment created further back than this is
    // essentially guaranteed to have delivered outside maxDays too, so
    // there's no point scanning further once we're past it.
    const TRANSIT_BUFFER_DAYS = 45;
    const scanDaysBack = maxDays + TRANSIT_BUFFER_DAYS;

    useEffect(() => {
        setVisibleCount(20);
    }, [searchQuery, stockFilter, groupByShipment]);

    // `loading` covers only the very first fetch (before `data` has ever
    // been set). Every load after that — filter changes, dismiss, tracking
    // a case, the silent background liveSync loop — goes through
    // `refreshing`, which never unmounts the filter bar or already-visible
    // results. `silent=true` skips both indicators entirely.
    const load = (silent) => {
        if (!silent) {
            if (data === null) setLoading(true);
            else setRefreshing(true);
        }
        const seq = ++requestSeqRef.current;
        return getInboundShortages(statusFilter, minDays, maxDays)
            .then((result) => {
                // A newer load() has since been kicked off (another filter
                // change, or another sync tick) — this response is stale,
                // so drop it rather than overwrite fresher data.
                if (seq !== requestSeqRef.current) return;
                setData(result);
            })
            .finally(() => {
                if (seq !== requestSeqRef.current) return;
                setLoading(false);
                setRefreshing(false);
            });
    };

    // Live sync (paging through Walmart's real shipment history) runs ONCE
    // per page visit — it's independent of the status/day-range filters,
    // which only narrow what's already synced locally. Re-running it on
    // every filter change was the bug: it made changing filters look like
    // it was "searching further" instead of just re-filtering.
    useEffect(() => {
        (async () => {
            await load(false);
            await liveSync.sync(2, () => load(true), scanDaysBack);
            setInitialScanDone(true);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Filter changes just re-fetch with the new params — no re-sync.
    useEffect(() => {
        if (!initialScanDone) return; // skip during the initial mount effect above, which already fetches
        load(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, minDays, maxDays]);

    // Widening maxDays widens scanDaysBack too — the hook detects the new
    // bound and resets its own paging state (see useLiveShipmentSync), so
    // just re-kick a couple of pages under the new, wider window. Narrowing
    // maxDays never needs this: results already synced under a wider bound
    // are still valid, just filtered down further in `load()`.
    useEffect(() => {
        if (!initialScanDone) return;
        liveSync.sync(2, () => load(true), scanDaysBack);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scanDaysBack]);

    // Persists across re-renders AND across data reloads within this page
    // session — a ref (not state), so it's not reset every time `data`
    // changes (e.g. after liveSync refreshes). Tracks "we already tried
    // enriching this row this session," so a genuinely unfixable row (no
    // GTIN/UPC/wpid, or Walmart search came back empty) doesn't get
    // re-attempted on every reload — exactly once per row per session.
    const enrichAttemptedRef = useRef(new Set());

    // Auto-fix any shortage that's missing its catalog link OR still has no
    // price OR still has no image — one at a time, gently staggered, same
    // pattern as image enrichment elsewhere in this app. Patches the row in
    // place the moment a match/image/price is found, so results fill in
    // live instead of requiring a manual reload. Each row is only ever
    // attempted once per session, success or failure. Runs regardless of
    // grouping mode — grouping is purely a display concern over the same
    // underlying `data.shortages` list.
    useEffect(() => {
        if (!data?.shortages?.length) return;
        const needsEnrichment = data.shortages.filter((s) => {
            const key = `${s.wfs_shipment_split_id}:${s.sku}`;
            if (enrichAttemptedRef.current.has(key)) return false;
            return !s.catalog_item_id || s.unit_price == null || s.unit_price === 0 || !s.image_url;
        });
        if (needsEnrichment.length === 0) return;
        let cancelled = false;
        (async () => {
            for (const s of needsEnrichment) {
                if (cancelled) return;
                const key = `${s.wfs_shipment_split_id}:${s.sku}`;
                enrichAttemptedRef.current.add(key); // marked BEFORE the call — one attempt, regardless of outcome
                const result = await enrichReimbursementLink(s.wfs_shipment_split_id, s.sku).catch(() => null);
                if (cancelled || !result?.linked) continue;
                setData((prev) => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        shortages: prev.shortages.map((row) =>
                            row.wfs_shipment_split_id === s.wfs_shipment_split_id && row.sku === s.sku
                                ? {
                                    ...row,
                                    catalog_item_id: result.catalog_item_id,
                                    unit_price: result.price ?? 0,
                                    estimated_value: (result.price ?? 0) * row.discrepancy_qty,
                                    missing_price: result.price == null,
                                    image_url: row.image_url || result.image_url,
                                    title: row.title || result.title,
                                    wpid: row.wpid || result.wpid,
                                }
                                : row
                        ),
                    };
                });
                await new Promise((r) => setTimeout(r, 250));
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.shortages?.length]);

    // Scrolling near the bottom digs one live page further into shipment
    // history — older closed shipments can surface new shortages that
    // wouldn't have shown up in the first couple of pages.
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver((entries) => {
            // Skip auto-continuing the background scan while there's an
            // active search — a narrowed list can visually hit the bottom
            // sentinel almost immediately, which would otherwise keep
            // kicking off more Walmart history scanning on every scroll
            // even though nothing is actually missing.
            if (entries[0].isIntersecting && !liveSync.done && !liveSync.syncing && !searchQuery.trim()) {
                liveSync.sync(1, () => load(true), scanDaysBack);
            }
        }, { rootMargin: "200px" });
        observer.observe(el);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveSync.done, liveSync.syncing, searchQuery, scanDaysBack]);

    const dismiss = async (s, key) => {
        setDismissing(key);
        try {
            await createReimbursementCase({
                source_type: "inbound_shortage",
                wfs_shipment_split_id: s.wfs_shipment_split_id,
                inbound_order_id: s.inbound_order_id, shipment_id: s.shipment_id,
                sku: s.sku, title: s.title, image_url: s.image_url,
                discrepancy_qty: s.discrepancy_qty, unit_cost: s.unit_price,
                estimated_value: s.estimated_value, status: "ignored",
            });
            load();
        } catch (e) {
            alert(e.message || "Failed to remove");
        } finally {
            setDismissing(null);
        }
    };

    const dismissGroup = async (group) => {
        setDismissingGroupId(group.wfs_shipment_split_id);
        try {
            await Promise.all(group.items.map((s) => createReimbursementCase({
                source_type: "inbound_shortage",
                wfs_shipment_split_id: s.wfs_shipment_split_id,
                inbound_order_id: s.inbound_order_id, shipment_id: s.shipment_id,
                sku: s.sku, title: s.title, image_url: s.image_url,
                discrepancy_qty: s.discrepancy_qty, unit_cost: s.unit_price,
                estimated_value: s.estimated_value, status: "ignored",
            })));
            load();
        } catch (e) {
            alert(e.message || "Failed to remove");
        } finally {
            setDismissingGroupId(null);
        }
    };

    // Only the very first-ever load blocks the whole tab. Once we have
    // `data` (even an empty result), filters/controls stay mounted for
    // good — "still scanning" / "no results" / "no matches" all render
    // INSIDE the results area below instead of replacing the page.
    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 text-teal-600 py-20">
                <Loader size={32} className="animate-spin" />
                <p className="text-base font-bold">Scanning your closed shipments for discrepancies...</p>
                <p className="text-xs text-gray-400">This can take a moment on the first visit.</p>
            </div>
        );
    }

    const shortages = data?.shortages || [];
    const stillScanning = shortages.length === 0 && !initialScanDone;

    const filteredShortages = shortages.filter((s) => {
        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            if (!(s.sku || "").toLowerCase().includes(q) && !(s.title || "").toLowerCase().includes(q)) {
                return false;
            }
        }
        if (stockFilter === "zero" && (s.received_qty || 0) !== 0) return false;
        if (stockFilter === "partial" && !((s.received_qty || 0) > 0)) return false;
        return true;
    });

    const groups = groupByShipment ? groupShortagesByShipment(filteredShortages) : null;
    const totalCount = groupByShipment ? groups.length : filteredShortages.length;
    const visibleGroups = groups ? groups.slice(0, visibleCount) : null;
    const visibleItems = !groupByShipment ? filteredShortages.slice(0, visibleCount) : null;
    const hasMore = visibleCount < totalCount;

    return (
        <div>
            {(refreshing || (!initialScanDone)) && (
                <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 text-teal-700 rounded-lg px-3 py-2 text-sm font-bold mb-3 sticky top-0 z-10 shadow-sm">
                    <Loader size={16} className="animate-spin" /> Updating results please wait…
                </div>
            )}

            <div className="flex flex-wrap items-center gap-2 mb-3">
                <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search SKU or title..."
                    className="flex-1 min-w-[180px] border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-teal-500" />
                <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)}
                    className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-teal-500 bg-white">
                    <option value="all">All items</option>
                    <option value="zero">Zero received (total loss)</option>
                    <option value="partial">Partially received</option>
                </select>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-teal-500 bg-white">
                    <option value="CLOSED">Closed</option>
                    <option value="AWAITING_DELIVERY">Awaiting Delivery</option>
                    <option value="RECEIVING_IN_PROGRESS">Receiving In Progress</option>
                    <option value="ALL">All Statuses</option>
                </select>
                <div className="flex items-center gap-1 text-xs text-gray-500 border border-gray-200 rounded-lg px-2 py-1">
                    <span>Delivered</span>
                    <input type="number" value={minDaysDraft}
                        onChange={(e) => setMinDaysDraft(e.target.value)}
                        onBlur={() => {
                            const parsed = Math.max(0, parseInt(minDaysDraft, 10) || 0);
                            setMinDaysDraft(String(parsed));
                            setMinDays(parsed);
                        }}
                        onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                        className="w-12 border border-gray-200 rounded px-1 py-0.5 text-xs" />
                    <span>–</span>
                    <input type="number" value={maxDaysDraft}
                        onChange={(e) => setMaxDaysDraft(e.target.value)}
                        onBlur={() => {
                            const parsed = Math.max(0, parseInt(maxDaysDraft, 10) || 0);
                            setMaxDaysDraft(String(parsed));
                            setMaxDays(parsed);
                        }}
                        onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                        className="w-12 border border-gray-200 rounded px-1 py-0.5 text-xs" />
                    <span>days ago</span>
                </div>
            </div>

            <div className="flex items-center justify-between mb-3">
                <label className="flex items-center gap-2 text-xs font-bold text-gray-600 cursor-pointer select-none">
                    <button type="button" onClick={() => setGroupByShipment((v) => !v)}
                        className={`relative w-9 h-5 rounded-full transition flex-shrink-0 ${groupByShipment ? "bg-teal-500" : "bg-gray-300"}`}>
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${groupByShipment ? "translate-x-4" : ""}`} />
                    </button>
                    Group by Shipment
                </label>
                <span className="text-xs text-gray-400">
                    {groupByShipment ? `${totalCount} shipment${totalCount !== 1 ? "s" : ""}` : `${totalCount} item${totalCount !== 1 ? "s" : ""}`}
                </span>
            </div>

            {stillScanning ? (
                <div className="flex flex-col items-center justify-center gap-3 bg-teal-50 border border-teal-200 rounded-xl text-teal-600 py-14">
                    <Loader size={28} className="animate-spin" />
                    <p className="text-sm font-bold">Still checking your shipment history for discrepancies...</p>
                </div>
            ) : shortages.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                    <Check size={32} className="mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No inbound shortages found - every closed shipment matches what you shipped.</p>
                </div>
            ) : totalCount === 0 ? (
                <p className="text-center py-10 text-gray-400 text-sm">No items match your filters.</p>
            ) : groupByShipment ? (
                <div className="space-y-2">
                    {visibleGroups.map((g) => (
                        <ShipmentGroupCard key={g.wfs_shipment_split_id} group={g} liveSync={liveSync}
                            onOpenPacket={setPacketFor} onDismissGroup={dismissGroup} dismissingGroupId={dismissingGroupId} />
                    ))}
                </div>
            ) : (
                <div className="space-y-2">
                    {visibleItems.map((s) => {
                        const key = `${s.wfs_shipment_split_id}:${s.sku}`;
                        return (
                            <div key={key} className="bg-white rounded-xl shadow p-4">
                                <div className="flex items-center gap-3">
                                    {s.wpid ? (
                                        <a href={`https://www.walmart.com/ip/${s.wpid}`} target="_blank" rel="noopener noreferrer">
                                            <ProductImage src={s.image_url} />
                                        </a>
                                    ) : (
                                        <ProductImage src={s.image_url} />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            {s.wpid ? (
                                                <a href={`https://www.walmart.com/ip/${s.wpid}`} target="_blank" rel="noopener noreferrer"
                                                    className="text-sm font-semibold text-gray-800 truncate hover:text-teal-600 hover:underline">
                                                    {s.title}
                                                </a>
                                            ) : (
                                                <p className="text-sm font-semibold text-gray-800 truncate">{s.title}</p>
                                            )}
                                            {!liveSync.done && !liveSync.touchedIds.has(s.inbound_order_id) && (
                                                <span className="flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-600 px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap flex-shrink-0">
                                                    <Loader size={12} className="animate-spin" /> Updating…
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-gray-400">
                                            SKU {s.sku} - {s.ship_to_address?.fcName} - Shipment {s.shipment_id}
                                        </p>
                                        <p className="text-xs text-red-500 font-semibold mt-0.5">
                                            Shipped {s.shipped_qty} to Received {s.received_qty} ({s.discrepancy_qty} short)
                                        </p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <p className="text-lg font-bold text-teal-700">{money(s.estimated_value)}</p>
                                        <p className="text-[10px] text-gray-400 mb-1">{money(s.unit_price)}/unit sale price</p>
                                        <button onClick={() => setPacketFor(singleItemGroup(s))} className="text-xs font-bold text-teal-600 hover:text-teal-700 underline decoration-dotted block mb-1">
                                            Get Evidence Packet
                                        </button>
                                        <button onClick={() => dismiss(s, key)} disabled={dismissing === key}
                                            className="text-[10px] text-gray-300 hover:text-red-500 transition">
                                            {dismissing === key ? "..." : "Ignore / Remove"}
                                        </button>
                                    </div>
                                </div>

                                {s.missing_price && (
                                    <div className="mt-2 bg-orange-50 border border-orange-200 rounded-lg px-2.5 py-1.5 text-[11px] text-orange-700 flex items-center gap-1.5">
                                        <AlertCircle size={12} /> No sale price on file for this item in your Catalog — value shown as $0.00 until you set one.
                                    </div>
                                )}

                                <div className="mt-2 pt-2 border-t border-gray-50 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-400">
                                    <span>Status: <span className="font-semibold text-gray-600">{s.split_status}</span>{s.split_status === "CLOSED" && " (finalized — no further units expected)"}</span>
                                    {s.actual_delivery_date && <span>Delivered {s.actual_delivery_date.slice(0, 10)}</span>}
                                    {s.updated_expected_delivery_date && !s.actual_delivery_date && <span>ETA was {s.updated_expected_delivery_date.slice(0, 10)}</span>}
                                    {s.carrier_name && <span>Carrier: {s.carrier_name}</span>}
                                    {s.order_created_date && <span>Shipped {s.order_created_date.slice(0, 10)}</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {hasMore && (
                <div className="flex justify-center mt-4">
                    <button onClick={() => setVisibleCount((v) => v + 20)}
                        className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-bold text-gray-600 hover:bg-gray-50 transition">
                        Load 20 More ({totalCount - visibleCount} remaining)
                    </button>
                </div>
            )}

            <div ref={sentinelRef} className="py-6 flex flex-col items-center justify-center gap-2">
                {liveSync.syncing && (
                    <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg px-4 py-2.5 text-sm font-bold">
                        <Loader size={16} className="animate-spin" />
                        Checked ~{liveSync.checkedCount} shipments within your date range, none found yet — checking further back…
                    </div>
                )}
                {liveSync.done && (
                    <span className="text-xs font-semibold text-gray-400">
                        {liveSync.isBounded
                            ? `Checked every shipment within your ${maxDays}-day delivered-date range — nothing further back would match your filter.`
                            : `Checked all ${liveSync.checkedCount > 0 ? `~${liveSync.checkedCount} ` : ""}shipments in your history.`}
                    </span>
                )}
            </div>

            {packetFor && <EvidencePacketModal group={packetFor} onClose={() => setPacketFor(null)} onTracked={load} />}
        </div>
    );
};

const OverdueTab = () => {
    const [overdue, setOverdue] = useState(null);
    const [initialScanDone, setInitialScanDone] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [dismissing, setDismissing] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const liveSync = useLiveShipmentSync();
    const sentinelRef = useRef(null);

    const load = () => getOverdueShipments().then((d) => setOverdue(d.overdue));
    useEffect(() => {
        (async () => {
            await load();
            await liveSync.sync(2, load);
            setInitialScanDone(true);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && !liveSync.done && !liveSync.syncing) {
                liveSync.sync(1, load);
            }
        }, { rootMargin: "200px" });
        observer.observe(el);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveSync.done, liveSync.syncing]);

    // Built from whatever statuses actually appear in the data — no
    // hardcoded list, so it always matches reality instead of guessing at
    // every possible value Walmart's status enum might return.
    const statusCounts = (overdue || []).reduce((acc, o) => {
        acc[o.status] = (acc[o.status] || 0) + 1;
        return acc;
    }, {});
    const availableStatuses = Object.keys(statusCounts).sort();
    const filtered = statusFilter === "all" ? overdue : (overdue || []).filter((o) => o.status === statusFilter);

    const dismiss = async (o) => {
        setDismissing(o.wfs_shipment_split_id);
        try {
            await createReimbursementCase({
                source_type: "overdue_watch",
                wfs_shipment_split_id: o.wfs_shipment_split_id,
                inbound_order_id: o.inbound_order_id, shipment_id: o.shipment_id,
                sku: "__SHIPMENT__", discrepancy_qty: 0, status: "ignored",
                ship_to_address: o.ship_to_address, split_status: o.status,
                order_created_date: o.order_created_date, carrier_name: o.carrier_name, tracking_no: o.tracking_no,
            });
            load();
        } catch (e) {
            alert(e.message || "Failed to dismiss");
        } finally {
            setDismissing(null);
        }
    };

    if (overdue === null || (overdue.length === 0 && !initialScanDone)) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 text-teal-600 py-20">
                <Loader size={32} className="animate-spin" />
                <p className="text-base font-bold">Checking for overdue shipments...</p>
            </div>
        );
    }
    if (overdue.length === 0) {
        return <div className="text-center py-16 text-gray-400"><Check size={32} className="mx-auto mb-2 opacity-40" /><p className="text-sm">Nothing overdue - everything in transit is still within its expected window.</p></div>;
    }

    return (
        <div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-xs text-amber-700 flex items-start gap-2">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                Sorted most-overdue first. Not a claim yet - but worth checking with your carrier. If one never arrives, it may become a total-loss reimbursement case.
            </div>

            <div className="flex gap-1.5 mb-4 flex-wrap">
                <button onClick={() => setStatusFilter("all")}
                    className={`px-3 py-1.5 text-xs font-bold rounded-full border-2 transition ${statusFilter === "all" ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 text-gray-400 hover:border-gray-300"}`}>
                    All ({overdue.length})
                </button>
                {availableStatuses.map((st) => (
                    <button key={st} onClick={() => setStatusFilter(st)}
                        className={`px-3 py-1.5 text-xs font-bold rounded-full border-2 transition ${statusFilter === st ? "border-teal-500 bg-teal-50 text-teal-700" : "border-gray-200 text-gray-400 hover:border-gray-300"}`}>
                        {st.replace(/_/g, " ")} ({statusCounts[st]})
                    </button>
                ))}
            </div>

            {filtered.length === 0 && <p className="text-gray-400 text-sm py-6 text-center">No shipments match this status.</p>}

            <div className="space-y-2">
                {filtered.map((o) => {
                    const isOpen = expanded === o.wfs_shipment_split_id;
                    return (
                        <div key={o.wfs_shipment_split_id} className="bg-white rounded-xl shadow overflow-hidden">
                            <div className="w-full p-4 flex items-center justify-between text-left">
                                <button onClick={() => setExpanded(isOpen ? null : o.wfs_shipment_split_id)} className="flex-1 text-left">
                                    <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                                        {o.ship_to_address?.fcName} - Shipment {o.shipment_id}
                                        {!liveSync.done && !liveSync.touchedIds.has(o.inbound_order_id) && (
                                            <span className="flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-600 px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap">
                                                <Loader size={12} className="animate-spin" /> Updating…
                                            </span>
                                        )}
                                    </p>
                                    <p className="text-xs text-gray-400">{o.ship_to_address?.city}, {o.ship_to_address?.stateCode} - {o.total_qty} units</p>
                                    <div className="text-xs text-gray-400 mt-1 space-y-0.5">
                                        <p>Status: <span className="font-semibold text-gray-600">{o.status}</span> - Original ETA {o.expected_delivery_date?.slice(0, 10)}{o.eta !== o.expected_delivery_date ? ` (updated to ${o.eta?.slice(0, 10)})` : ""}</p>
                                        {o.order_created_date && <p>Shipped {o.order_created_date.slice(0, 10)}</p>}
                                        {o.carrier_name && <p>Carrier: {o.carrier_name}</p>}
                                        {o.tracking_no?.length > 0 && <p className="font-mono">Tracking: {o.tracking_no.join(", ")}</p>}
                                    </div>
                                </button>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                    <span className="text-xs font-bold px-2 py-1 rounded-full bg-orange-50 text-orange-600">{o.days_overdue}d overdue</span>
                                    <button onClick={() => dismiss(o)} disabled={dismissing === o.wfs_shipment_split_id}
                                        className="text-[10px] text-gray-300 hover:text-red-500 transition">
                                        {dismissing === o.wfs_shipment_split_id ? "..." : "Dismiss"}
                                    </button>
                                    <button onClick={() => setExpanded(isOpen ? null : o.wfs_shipment_split_id)} className="text-gray-300 hover:text-gray-500">
                                        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                    </button>
                                </div>
                            </div>
                            {isOpen && (
                                <div className="px-4 pb-4">
                                    <table className="w-full text-xs">
                                        <thead><tr className="text-gray-400 text-left border-b border-gray-100"><th className="py-1.5">Product</th><th>SKU</th><th>Qty</th></tr></thead>
                                        <tbody>
                                            {o.items.map((it) => (
                                                <tr key={it.sku} className="border-b border-gray-50">
                                                    <td className="py-1.5 flex items-center gap-2"><ProductImage src={it.image_url} />{it.title}</td>
                                                    <td className="font-mono text-gray-500">{it.sku}</td>
                                                    <td>{it.item_qty}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div ref={sentinelRef} className="py-6 flex flex-col items-center justify-center gap-2">
                {liveSync.syncing && (
                    <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg px-4 py-2.5 text-sm font-bold">
                        <Loader size={16} className="animate-spin" />
                        Checked ~{liveSync.checkedCount} recent shipments, none found yet — checking further back…
                    </div>
                )}
                {liveSync.done && (
                    <span className="text-xs font-semibold text-gray-400">
                        Checked all {liveSync.checkedCount > 0 ? `~${liveSync.checkedCount} ` : ""}shipments in your history.
                    </span>
                )}
            </div>
        </div>
    );
};

// Buckets tracked cases by shipment for display only — every action still
// operates on the individual case row underneath, nothing here changes
// what gets sent to the backend. wfs_shipment_split_id is the primary key;
// falls back to inbound_order_id (covers rows where the split id might be
// missing), then the case's own id as a last resort so a case never gets
// silently dropped from view.
const groupCasesByShipment = (cases) => {
    const groups = new Map();
    for (const c of cases) {
        const key = c.wfs_shipment_split_id || c.inbound_order_id || `case:${c.id}`;
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                inbound_order_id: c.inbound_order_id,
                shipment_id: c.shipment_id,
                ship_to_address: c.ship_to_address,
                split_status: c.split_status,
                order_created_date: c.order_created_date,
                actual_delivery_date: c.actual_delivery_date,
                carrier_name: c.carrier_name,
                cases: [],
                total_estimated_value: 0,
                total_granted_amount: 0,
                any_granted: false, // true once at least one case in this group has a granted amount entered
            });
        }
        const g = groups.get(key);
        g.cases.push(c);
        g.total_estimated_value += c.estimated_value || 0;
        if (c.granted_amount != null) {
            g.total_granted_amount += c.granted_amount;
            g.any_granted = true;
        }
    }
    return Array.from(groups.values());
};

const CaseRow = ({
    c, editingId, caseNumber, setEditingId, setCaseNumber, setStatus, saveCaseNumber, remove,
    editingGrantedId, grantedDraft, setEditingGrantedId, setGrantedDraft, saveGrantedAmount,
}) => (
    <div className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-b-0">
        <ProductImage src={c.image_url} />
        <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">{c.title || (c.source_type === "overdue_watch" ? `Shipment ${c.shipment_id}` : c.sku)}</p>
            <p className="text-xs text-gray-400">
                {c.source_type === "overdue_watch" ? "Overdue watch" : `SKU ${c.sku} - ${c.discrepancy_qty} units - ${money(c.estimated_value)}`}
            </p>
            {editingId === c.id ? (
                <div className="flex items-center gap-1.5 mt-1">
                    <input value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)} placeholder="Seller Center case #"
                        className="border border-gray-200 rounded px-2 py-1 text-xs w-40" />
                    <button onClick={() => saveCaseNumber(c.id)} className="text-xs text-teal-600 font-bold">Save</button>
                    <button onClick={() => setEditingId(null)} className="text-xs text-gray-400">✕</button>
                </div>
            ) : (
                <button onClick={() => { setEditingId(c.id); setCaseNumber(c.walmart_case_number || ""); }}
                    className="text-xs text-gray-400 hover:text-teal-600 underline decoration-dotted mt-0.5">
                    {c.walmart_case_number ? `Case #${c.walmart_case_number}` : "+ Add case number"}
                </button>
            )}

            {editingGrantedId === c.id ? (
                <div className="flex items-center gap-1.5 mt-1">
                    <span className="text-xs text-gray-400">$</span>
                    <input value={grantedDraft} onChange={(e) => setGrantedDraft(e.target.value)}
                        inputMode="decimal" placeholder="Amount granted" autoFocus
                        className="border border-gray-200 rounded px-2 py-1 text-xs w-24" />
                    <button onClick={() => saveGrantedAmount(c.id)} className="text-xs text-teal-600 font-bold">Save</button>
                    <button onClick={() => setEditingGrantedId(null)} className="text-xs text-gray-400">✕</button>
                </div>
            ) : (
                <div className="flex items-center gap-1 mt-0.5">
                    <span className="text-xs text-gray-500">Granted: {c.granted_amount != null ? money(c.granted_amount) : "—"}</span>
                    <button
                        onClick={() => { setEditingGrantedId(c.id); setGrantedDraft(c.granted_amount != null ? String(c.granted_amount) : ""); }}
                        title="Edit granted amount"
                        className="text-gray-300 hover:text-teal-600 transition"
                    >
                        <Pencil size={11} />
                    </button>
                </div>
            )}
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            <select value={c.status} onChange={(e) => setStatus(c.id, e.target.value)}
                className={`text-xs font-bold px-2 py-1 rounded-full border-none ${CASE_STATUS_STYLES[c.status]}`}>
                <option value="identified">Identified</option>
                <option value="filed">Filed</option>
                <option value="resolved">Resolved</option>
                <option value="denied">Denied</option>
                <option value="ignored">Ignored</option>
            </select>
            <button onClick={() => remove(c.id)} className="text-[10px] text-gray-300 hover:text-red-500">Delete</button>
        </div>
    </div>
);

const CaseGroupCard = ({ group, ...rowProps }) => {
    const [expanded, setExpanded] = useState(true);
    const multi = group.cases.length > 1;
    return (
        <div className="bg-white rounded-xl shadow p-4">
            <button onClick={() => setExpanded((e) => !e)} className="w-full flex items-center justify-between text-left mb-1">
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">
                        {group.ship_to_address?.fcName || "Unknown FC"}{group.shipment_id ? ` · Shipment ${group.shipment_id}` : ""}
                    </p>
                    <p className="text-xs text-gray-400">
                        {group.cases.length} case{multi ? "s" : ""}{group.inbound_order_id ? ` - ${group.inbound_order_id}` : ""}
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                        {multi && <p className="text-sm font-bold text-teal-700">{money(group.total_estimated_value)}</p>}
                        <p className="text-[10px] font-semibold text-gray-400">
                            Granted: {group.any_granted ? money(group.total_granted_amount) : "—"}
                        </p>
                    </div>
                    {expanded ? <ChevronUp size={16} className="text-gray-300" /> : <ChevronDown size={16} className="text-gray-300" />}
                </div>
            </button>

            {expanded && (
                <div>
                    {group.cases.map((c) => <CaseRow key={c.id} c={c} {...rowProps} />)}
                </div>
            )}

            <div className="mt-2 pt-2 border-t border-gray-50 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-400">
                {group.split_status && <span>Shipment status: {group.split_status}</span>}
                {group.actual_delivery_date && <span>Delivered {group.actual_delivery_date.slice(0, 10)}</span>}
                {group.order_created_date && <span>Shipped {group.order_created_date.slice(0, 10)}</span>}
                {group.carrier_name && <span>Carrier: {group.carrier_name}</span>}
            </div>
        </div>
    );
};

const TrackedCasesTab = () => {
    const [cases, setCases] = useState(null);
    const [showResolved, setShowResolved] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [caseNumber, setCaseNumber] = useState("");
    const [editingGrantedId, setEditingGrantedId] = useState(null);
    const [grantedDraft, setGrantedDraft] = useState("");
    const [groupByShipment, setGroupByShipment] = useState(true);

    const load = () => listReimbursementCases().then((d) => setCases(d.cases));
    useEffect(() => { load(); }, []);

    const setStatus = async (id, status) => { await updateReimbursementCase(id, { status }); load(); };
    const saveCaseNumber = async (id) => { await updateReimbursementCase(id, { walmart_case_number: caseNumber }); setEditingId(null); load(); };
    const saveGrantedAmount = async (id) => {
        const trimmed = grantedDraft.trim();
        const numeric = trimmed === "" ? null : parseFloat(trimmed);
        if (trimmed !== "" && Number.isNaN(numeric)) return; // ignore invalid input, editor stays open
        await updateReimbursementCase(id, { granted_amount: numeric });
        setEditingGrantedId(null);
        load();
    };
    const remove = async (id) => {
        if (!window.confirm("Permanently delete this tracked case? This doesn't affect anything on Walmart's side.")) return;
        await deleteReimbursementCase(id);
        load();
    };

    if (cases === null) return <div className="flex items-center gap-2 text-gray-400 text-sm py-10"><Loader size={16} className="animate-spin" /> Loading...</div>;

    const DONE_STATUSES = ["resolved", "denied", "ignored"];
    const active = cases.filter((c) => !DONE_STATUSES.includes(c.status));
    const done = cases.filter((c) => DONE_STATUSES.includes(c.status));
    const visible = showResolved ? done : active;

    const totalOwed = active.reduce((s, c) => s + (c.estimated_value || 0), 0);
    // Only actual "resolved" counts toward money recovered — denied and
    // ignored are done, but not money you got back, so they'd overstate
    // recovery if lumped in. Uses the actual granted_amount once it's been
    // entered; falls back to the pre-resolution estimate for cases marked
    // resolved before anyone recorded what Walmart actually paid.
    const totalResolved = cases.filter((c) => c.status === "resolved")
        .reduce((s, c) => s + (c.granted_amount != null ? c.granted_amount : (c.estimated_value || 0)), 0);
    // Sum of actual granted amounts entered so far, across ALL cases
    // regardless of status — this is the real, confirmed number (vs.
    // totalResolved, which still falls back to the estimate for
    // resolved cases nobody has recorded a granted amount for yet).
    const totalGranted = cases.reduce((s, c) => s + (c.granted_amount != null ? c.granted_amount : 0), 0);

    if (cases.length === 0) return <p className="text-center py-16 text-gray-400 text-sm">No cases tracked yet - get an evidence packet from the Inbound Shortages tab to start one.</p>;

    const rowProps = {
        editingId, caseNumber, setEditingId, setCaseNumber, setStatus, saveCaseNumber, remove,
        editingGrantedId, grantedDraft, setEditingGrantedId, setGrantedDraft, saveGrantedAmount,
    };
    const groups = groupByShipment ? groupCasesByShipment(visible) : null;

    return (
        <div>
            <div className="bg-white rounded-xl shadow p-4 mb-4 flex items-center justify-between">
                <div className="flex items-center gap-6">
                    <div>
                        <span className="text-sm text-gray-600 font-semibold">Pending / Filed value</span>
                        <p className="text-xl font-bold text-teal-700">{money(totalOwed)}</p>
                    </div>
                    <div>
                        <span className="text-sm text-gray-600 font-semibold">Resolved value</span>
                        <p className="text-xl font-bold text-green-600">{money(totalResolved)}</p>
                    </div>
                    <div>
                        <span className="text-sm text-gray-600 font-semibold">Granted</span>
                        <p className="text-xl font-bold text-purple-600">{money(totalGranted)}</p>
                    </div>
                </div>
                <button onClick={() => setShowResolved((s) => !s)}
                    className="text-xs font-bold text-gray-500 hover:text-teal-600 underline decoration-dotted">
                    {showResolved ? `< Back to Active (${active.length})` : `View Resolved/Denied/Ignored (${done.length})`}
                </button>
            </div>

            <div className="flex items-center justify-between mb-3">
                <label className="flex items-center gap-2 text-xs font-bold text-gray-600 cursor-pointer select-none">
                    <button type="button" onClick={() => setGroupByShipment((v) => !v)}
                        className={`relative w-9 h-5 rounded-full transition flex-shrink-0 ${groupByShipment ? "bg-teal-500" : "bg-gray-300"}`}>
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${groupByShipment ? "translate-x-4" : ""}`} />
                    </button>
                    Group by Shipment
                </label>
                <span className="text-xs text-gray-400">
                    {groupByShipment ? `${groups.length} shipment${groups.length !== 1 ? "s" : ""}` : `${visible.length} case${visible.length !== 1 ? "s" : ""}`}
                </span>
            </div>

            {visible.length === 0 && (
                <p className="text-center py-10 text-gray-400 text-sm">{showResolved ? "Nothing resolved yet." : "No active cases."}</p>
            )}

            {groupByShipment ? (
                <div className="space-y-2">
                    {groups.map((g) => <CaseGroupCard key={g.key} group={g} {...rowProps} />)}
                </div>
            ) : (
                <div className="space-y-2">
                    {visible.map((c) => (
                        <div key={c.id} className="bg-white rounded-xl shadow p-4">
                            <CaseRow c={c} {...rowProps} />
                            <div className="mt-2 pt-2 border-t border-gray-50 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-400">
                                {c.ship_to_address?.fcName && <span>FC: {c.ship_to_address.fcName}</span>}
                                {c.split_status && <span>Shipment status: {c.split_status}</span>}
                                {c.actual_delivery_date && <span>Delivered {c.actual_delivery_date.slice(0, 10)}</span>}
                                {c.order_created_date && <span>Shipped {c.order_created_date.slice(0, 10)}</span>}
                                {c.carrier_name && <span>Carrier: {c.carrier_name}</span>}
                                <span>Identified {new Date(c.identified_at).toLocaleDateString()}</span>
                                {c.filed_at && <span>Filed {new Date(c.filed_at).toLocaleDateString()}</span>}
                                {c.resolved_at && <span>Resolved {new Date(c.resolved_at).toLocaleDateString()}</span>}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const ReimbursementsPage = () => {
    const [tab, setTab] = useState("shortages");

    const TABS = [
        { id: "shortages", label: "Inbound Shortages", icon: Package },
        { id: "overdue", label: "Not Yet Received", icon: Clock },
        { id: "cases", label: "Tracked Cases", icon: DollarSign },
    ];

    return (
        <div className="max-w-4xl mx-auto py-8 px-6">
            <h1 className="text-2xl font-bold text-gray-800 mb-1">Reimbursements</h1>
            <p className="text-gray-500 text-sm mb-6">Find inventory Walmart owes you for, and track every case through to resolution.</p>

            <div className="flex gap-1 mb-6 border-b border-gray-200 overflow-x-auto">
                {TABS.map((t) => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                        className={`px-4 py-2.5 text-sm font-bold border-b-2 transition flex items-center gap-1.5 whitespace-nowrap ${tab === t.id ? "border-teal-500 text-teal-700" : "border-transparent text-gray-400 hover:text-gray-600"}`}>
                        <t.icon size={14} /> {t.label}
                    </button>
                ))}
            </div>

            {tab === "shortages" && <InboundShortagesTab />}
            {tab === "overdue" && <OverdueTab />}
            {tab === "cases" && <TrackedCasesTab />}
        </div>
    );
};

export default ReimbursementsPage;